import { AgentSkillModel } from '@shared/models/agentExtensions/AgentSkillModel';
import {
    AgentExtensionFilterRequest,
    CreateAgentSkillRequest,
    EnableAgentExtensionRequest,
    PublishAgentExtensionRequest,
    UpdateAgentSkillRequest,
} from '@shared/models/agentExtensions/AgentExtensionRequests';
import {
    AgentExtensionSortBy,
    AgentSkillCommentModel,
    AgentSkillRatingModel,
    CreateAgentExtensionCommentRequest,
    OrganizationSkillUsageModel,
    RateAgentExtensionRequest,
} from '@shared/models/agentExtensions/AgentExtensionGovernanceModels';
import {
    AgentExtensionSubmissionModel,
    SubmitForReviewRequest,
} from '@shared/models/agentExtensions/AgentExtensionSubmissionModel';
import { PackageDownloadModel } from '@shared/models/agentExtensions/PackageDownloadModel';
import { UserEnabledSkillModel } from '@shared/models/agentExtensions/UserEnabledSkillModel';
import { sendDelete, sendGet, sendGetFile, sendPost, sendPut, uploadFile } from '@shared/api/generic';

/**
 * REST client for the API AI-Skill endpoints (`/api/skill/...`). Flat functions returning
 * `IResult<T>`, matching the `enabledPlayApi.ts` convention. Auth (JWT) is attached by `generic.ts`.
 */

const appendQuery = (params: Record<string, string | number | null | undefined>): string => {
    const search = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
        if (value === null || value === undefined || value === '') return;
        search.append(key, value.toString());
    });
    const q = search.toString();
    return q ? `?${q}` : '';
};

export const getOfficialSkills = (modifiedSince?: string, sortBy?: AgentExtensionSortBy) =>
    sendGet<AgentSkillModel[]>(`/api/skill/official${appendQuery({ modifiedSince, sortBy })}`);

export const getFeaturedSkills = (modifiedSince?: string, sortBy?: AgentExtensionSortBy) =>
    sendGet<AgentSkillModel[]>(`/api/skill/featured${appendQuery({ modifiedSince, sortBy })}`);

export const getPublicSkills = (
    filter: AgentExtensionFilterRequest,
    skip = 0,
    take = 25,
    modifiedSince?: string,
    sortBy?: AgentExtensionSortBy
) =>
    sendGet<AgentSkillModel[]>(
        `/api/skill/public${appendQuery({
            searchPhrase: filter?.searchPhrase,
            category: filter?.category,
            skip,
            take,
            modifiedSince,
            sortBy,
        })}`
    );

export const getOrganizationSkills = (organizationId: string, modifiedSince?: string, sortBy?: AgentExtensionSortBy) =>
    sendGet<AgentSkillModel[]>(`/api/skill/organization/${organizationId}${appendQuery({ modifiedSince, sortBy })}`);

/** Org-admin usage view: which members have each org skill enabled. */
export const getOrganizationSkillUsage = (organizationId: string) =>
    sendGet<OrganizationSkillUsageModel[]>(`/api/skill/organization/${organizationId}/usage`);

export const getMySkills = () => sendGet<AgentSkillModel[]>(`/api/skill/mine`);

export const getSkillById = (id: string) => sendGet<AgentSkillModel>(`/api/skill/${id}`);

export const getSkillPackageDownload = (id: string) => sendGet<PackageDownloadModel>(`/api/skill/${id}/package`);

/**
 * Downloads the package zip bytes through the API (same-origin, CORS-allowed). Web clients use this
 * instead of fetching the private-blob SAS URI directly, which the browser blocks by CORS.
 */
export const getSkillPackageContent = (id: string) => sendGetFile(`/api/skill/${id}/package/content`);

export const createSkill = (request: CreateAgentSkillRequest) => sendPost<AgentSkillModel>(`/api/skill`, request);

export const updateSkill = (id: string, request: UpdateAgentSkillRequest) =>
    sendPut<AgentSkillModel>(`/api/skill/${id}`, request);

export const deleteSkill = (id: string) => sendDelete<AgentSkillModel>(`/api/skill/${id}`);

export const uploadSkillPackage = (id: string, file: File) =>
    uploadFile<AgentSkillModel>(`/api/skill/${id}/package`, file);

export const publishSkill = (id: string, request: PublishAgentExtensionRequest) =>
    sendPost<AgentSkillModel>(`/api/skill/${id}/publish`, request);

// ----- public-review submissions -----

/** Submit an owned skill to Cephable for public-marketplace review (license-gated; does not publish immediately). */
export const submitSkillForReview = (id: string, request: SubmitForReviewRequest = {}) =>
    sendPost<AgentExtensionSubmissionModel>(`/api/skill/${id}/submit`, request);

/** Withdraw a pending submission and/or unlist a published skill. */
export const withdrawSkill = (id: string) => sendPost<AgentExtensionSubmissionModel>(`/api/skill/${id}/withdraw`, {});

/** The current (latest) review submission/status for an owned skill. */
export const getSkillSubmission = (id: string) => sendGet<AgentExtensionSubmissionModel>(`/api/skill/${id}/submission`);

/** All of the caller's skill/connector review submissions, newest first. */
export const getMySubmissions = (skip = 0, take = 25) =>
    sendGet<AgentExtensionSubmissionModel[]>(`/api/skill/mine/submissions${appendQuery({ skip, take })}`);

export const enableSkill = (id: string, request: EnableAgentExtensionRequest) =>
    sendPost<UserEnabledSkillModel>(`/api/skill/${id}/enable`, request);

export const disableSkill = (id: string, userDeviceId?: string) =>
    sendDelete<UserEnabledSkillModel>(`/api/skill/${id}/enable${appendQuery({ userDeviceId })}`);

export const getEnabledSkills = (userDeviceId?: string) =>
    sendGet<UserEnabledSkillModel[]>(`/api/skill/enabled${appendQuery({ userDeviceId })}`);

// ----- public (marketplace) ratings -----

export const getPublicSkillRatings = (skillId: string) =>
    sendGet<AgentSkillRatingModel[]>(`/api/skill/${skillId}/ratings`);

export const ratePublicSkill = (skillId: string, request: RateAgentExtensionRequest) =>
    sendPost<AgentSkillRatingModel>(`/api/skill/${skillId}/ratings`, request);

export const deletePublicSkillRating = (skillId: string) =>
    sendDelete<AgentSkillRatingModel>(`/api/skill/${skillId}/ratings`);

// ----- org-internal ratings & comments -----

export const getSkillRatings = (skillId: string, organizationId: string) =>
    sendGet<AgentSkillRatingModel[]>(`/api/skill/${skillId}/organization/${organizationId}/ratings`);

export const rateSkill = (skillId: string, organizationId: string, request: RateAgentExtensionRequest) =>
    sendPost<AgentSkillRatingModel>(`/api/skill/${skillId}/organization/${organizationId}/ratings`, request);

export const deleteSkillRating = (skillId: string, organizationId: string) =>
    sendDelete<AgentSkillRatingModel>(`/api/skill/${skillId}/organization/${organizationId}/ratings`);

export const getSkillComments = (skillId: string, organizationId: string) =>
    sendGet<AgentSkillCommentModel[]>(`/api/skill/${skillId}/organization/${organizationId}/comments`);

export const createSkillComment = (
    skillId: string,
    organizationId: string,
    request: CreateAgentExtensionCommentRequest
) => sendPost<AgentSkillCommentModel>(`/api/skill/${skillId}/organization/${organizationId}/comments`, request);

export const deleteSkillComment = (commentId: string) =>
    sendDelete<AgentSkillCommentModel>(`/api/skill/comments/${commentId}`);
